import React from 'react'
import { observer } from 'mobx-react-lite'
import { Instance } from 'mobx-state-tree'
import { View, ViewStyle } from 'react-native'

import { Branding } from '@trivie/core/src/models/Branding'
import { isReviewInfoCard } from '@trivie/core/src/models/stores/game-store'

import { QuestionImage } from '../question-image'
import { Text } from '../text'
import { ReviewMatching } from './review-matching-question'
import { ReviewMultipleChoice } from './review-mc-question'
import { ReviewOpenText } from './review-open-text-question'
import { ReviewOrdering } from './review-ordering-question'
import { ReviewPanelHeader } from './review-panel-header'

interface ReviewPanelProps {
  item: any
  branding?: Instance<typeof Branding>
  onClickInfoCard: (id: number) => void
}
export const ReviewPanel = observer((props: ReviewPanelProps) => {
  const { item, branding, onClickInfoCard } = props

  const multi = item?.question_answer_choices?.filter((c: any) => c.correct).length > 1

  const renderContent = () => {
    switch (item?.question_type) {
      case 'Open Text':
        return <ReviewOpenText branding={branding} question={item} />
      case 'Matching':
        return <ReviewMatching question={item} branding={branding} />
      case 'Ordering':
        return <ReviewOrdering question={item} branding={branding} />
      default:
        return item?.question_answer_choices?.map((choice: any, index: number) => {
          const userChoices = item?.user_answer_choices?.map(
            (userAnswer: any) => userAnswer?.choice_id,
          )
          const last = index === item?.question_answer_choices.length - 1
          return (
            <ReviewMultipleChoice
              style={{ marginBottom: last ? 0 : 25 }}
              choice={choice}
              multi={multi}
              selected={userChoices?.includes(choice?.choice_id)}
              branding={branding}
            />
          )
        })
    }
  }

  const renderAlternateAnswers = () => {
    const alt = item.question_answer_choices.filter((c) => c.correct).map((c) => c.choice_text)
    return (
      <Text
        style={{ color: branding!.review_background_text }}
        text={`Alternative answers: ${alt.join(', ')}`}
      />
    )
  }

  return (
    <View style={{ ...QUESTION_CONTAINER, backgroundColor: branding!.review_background }}>
      <ReviewPanelHeader onClickInfoCard={onClickInfoCard} item={item} branding={branding} />
      {isReviewInfoCard(item) ? null : (
        <>
          <View style={DIVIDER} />
          <QuestionImage url={item.image_url} imgStyle={{ marginBottom: 20, aspectRatio: 1.72 }} />
          {renderContent()}
          {item?.factoid ? (
            <>
              <View style={{ ...DIVIDER, marginTop: 20, marginBottom: 20 }} />
              <Text
                style={{
                  marginBottom: item?.question_type === 'Open Text' ? 20 : 0,
                  color: branding!.review_background_text,
                }}
                text={item.factoid.text}
              />
              {item?.question_type === 'Open Text' && renderAlternateAnswers()}
            </>
          ) : item?.question_type === 'Open Text' ? (
            <>
              <View style={{ ...DIVIDER, marginTop: 20, marginBottom: 20 }} />
              {renderAlternateAnswers()}
            </>
          ) : null}
        </>
      )}
    </View>
  )
})

const QUESTION_CONTAINER: ViewStyle = {
  flexDirection: 'column',
  marginBottom: 20,
  display: 'flex',
  justifyContent: 'space-between',
  padding: 20,
  borderRadius: 20,
}
const DIVIDER: ViewStyle = {
  height: 1,
  backgroundColor: '#ffffff20',
  marginBottom: 20,
  width: '100%',
}
